(() => {
  'use strict';

  const PATCH_FLAG = '__dpGuidanceResultPatched';

  const normalize = (value) => String(value ?? '').trim().toLowerCase();

  function displayLabel(instance, size) {
    if (!size) return '';
    if (typeof instance.dpDisplaySizeLabel === 'function') {
      const mapped = instance.dpDisplaySizeLabel(size);
      if (mapped) return String(mapped).trim();
    }
    return String(size.label ?? size ?? '').trim();
  }

  function hasExactMeasurements(instance, result) {
    const sources = result?.body?.sources || {};
    const profileType = normalize(instance?.config?.profileType);

    if (profileType === 'bottom') {
      return sources.waist === 'exact' && sources.hip === 'exact';
    }

    if (profileType === 'top') {
      return sources.chest === 'exact';
    }

    return false;
  }

  function findSizeChartTrigger(instance) {
    const drawer = instance.closest('x-drawer');
    const currentId = String(drawer?.id || '');
    const targetId = currentId.startsWith('find-size-')
      ? currentId.replace(/^find-size-/, 'size-chart-')
      : '';

    if (targetId) {
      const exact = Array.from(document.querySelectorAll('button[aria-controls]')).find(
        (button) => button.getAttribute('aria-controls') === targetId
      );
      if (exact) return exact;
    }

    const sectionId = String(instance?.config?.sectionId || '');
    return Array.from(document.querySelectorAll('button[aria-controls^="size-chart-"]')).find((button) => {
      const controls = button.getAttribute('aria-controls') || '';
      return !sectionId || controls.includes(`size-chart-${sectionId}-`);
    }) || null;
  }

  function ensureGuidanceUi(instance, result) {
    const noMatch = instance.querySelector('[data-size-no-match]');
    if (!noMatch) return;

    noMatch.classList.add('dp-guidance-result');

    const eyebrow = noMatch.querySelector('.size-recommender__eyebrow');
    const heading = noMatch.querySelector('h2');
    const copy = noMatch.querySelector('[data-size-no-match-copy]');
    const closestSize = result?.closest?.size || null;
    const shown = displayLabel(instance, closestSize);
    const exact = hasExactMeasurements(instance, result);
    const profileType = normalize(instance?.config?.profileType);

    if (eyebrow) eyebrow.textContent = 'Fit guidance';

    if (closestSize && shown) {
      if (heading) heading.textContent = `Recommended starting point: ${shown}`;

      if (copy && !exact) {
        copy.textContent = profileType === 'bottom'
          ? `Size ${shown} is the closest match based on your profile. Because exact waist and hip measurements weren’t provided, we can’t confirm the ideal fit with full accuracy.`
          : `Size ${shown} is the closest match based on your profile. Because exact chest measurements weren’t provided, we can’t confirm the ideal fit with full accuracy.`;
      } else if (copy) {
        copy.textContent = String(copy.textContent || '')
          .replace(`Size ${shown} is closest,`, `Size ${shown} is the closest match,`)
          .replace('is the closest option,', 'is the closest match,');
      }
    } else {
      if (heading) heading.textContent = 'Check the size chart';
      if (copy) {
        copy.textContent = 'We couldn’t identify a reliable starting point from this profile. Compare your measurements with the garment chart for the best match.';
      }
    }

    let note = noMatch.querySelector('[data-dp-guidance-note]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'size-recommender__guidance-note';
      note.dataset.dpGuidanceNote = '';
      noMatch.append(note);
    }

    note.textContent = exact
      ? 'For the best result, compare the garment measurements with a pair you already own before ordering.'
      : 'For the best result, compare your measurements with the size chart or enter them exactly.';

    const originalRestart = noMatch.querySelector('[data-size-action="restart"]');
    if (originalRestart) originalRestart.hidden = true;

    let actions = noMatch.querySelector('[data-dp-guidance-actions]');
    if (!actions) {
      actions = document.createElement('div');
      actions.className = 'size-recommender__guidance-actions';
      actions.dataset.dpGuidanceActions = '';

      const sizeChartButton = document.createElement('button');
      sizeChartButton.type = 'button';
      sizeChartButton.className = 'button size-recommender__primary size-recommender__guidance-primary';
      sizeChartButton.dataset.dpSizeChart = '';
      sizeChartButton.textContent = 'Check size chart';

      const measurementsButton = document.createElement('button');
      measurementsButton.type = 'button';
      measurementsButton.className = 'size-recommender__guidance-secondary';
      measurementsButton.dataset.dpExactMeasurements = '';

      actions.append(sizeChartButton, measurementsButton);
      noMatch.append(actions);
    }

    const chartButton = actions.querySelector('[data-dp-size-chart]');
    if (chartButton) chartButton.hidden = !findSizeChartTrigger(instance);

    const measurementsButton = actions.querySelector('[data-dp-exact-measurements]');
    if (measurementsButton) {
      measurementsButton.textContent = exact ? 'Edit measurements' : 'Enter exact measurements';
    }
  }

  function openSizeChart(instance) {
    const trigger = findSizeChartTrigger(instance);
    if (!trigger) return;

    const drawer = instance.closest('x-drawer');
    const openChart = () => trigger.click();

    if (drawer && typeof drawer.hide === 'function') {
      drawer.hide();
      window.setTimeout(openChart, 140);
      return;
    }

    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
      window.setTimeout(openChart, 140);
      return;
    }

    openChart();
  }

  function editMeasurements(instance) {
    const steps = Array.isArray(instance.steps) ? instance.steps : [];
    if (!steps.length || typeof instance.showStep !== 'function') return;

    instance.showStep(steps.length - 1, false);

    const profileType = normalize(instance?.config?.profileType);
    const fieldName = profileType === 'bottom' ? 'body_waist' : 'body_chest';
    const field = instance.form?.elements?.[fieldName];

    window.requestAnimationFrame(() => {
      field?.focus({ preventScroll: true });
      field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  function patchGuidance() {
    const Recommender = customElements.get('size-recommender');
    if (!Recommender || Recommender[PATCH_FLAG]) return;

    Recommender[PATCH_FLAG] = true;
    const proto = Recommender.prototype;

    const originalOnClick = proto.onClick;
    if (typeof originalOnClick === 'function') {
      proto.onClick = function onClick(event) {
        if (event.target.closest?.('[data-dp-size-chart]')) {
          openSizeChart(this);
          return;
        }

        if (event.target.closest?.('[data-dp-exact-measurements]')) {
          editMeasurements(this);
          return;
        }

        return originalOnClick.call(this, event);
      };
    }

    const originalRenderResult = proto.renderResult;
    if (typeof originalRenderResult === 'function') {
      proto.renderResult = function renderResult(result, options) {
        const value = originalRenderResult.call(this, result, options);
        if (result && !result.matched) ensureGuidanceUi(this, result);
        return value;
      };
    }

    document.querySelectorAll('size-recommender').forEach((instance) => {
      if (instance.lastResult && typeof instance.renderResult === 'function') {
        instance.renderResult(instance.lastResult, { moveFocus: false });
      }
    });
  }

  if (customElements.get('size-recommender')) {
    queueMicrotask(patchGuidance);
  } else {
    customElements.whenDefined('size-recommender').then(() => queueMicrotask(patchGuidance));
  }
})();